import type {CallAudioRecording,Session} from '@bcio-voice/contracts';
import {ApiRequestError,type ApiClient,type DataEnvelope} from '../../lib/api.ts';
import {currentRoleIds} from '../admin/common/roles.ts';

export const canManageCallRecording=(session:Session):boolean=>!session.view_as&&!!session.tenant
  &&currentRoleIds(session).some(role=>role==='owner'||role==='admin');
export type CallRecordingState={value:CallAudioRecording|null;phase:'loading'|'ready'|'saving'|'error';message:string;reauthenticate:boolean};
const PATH='/tenant/call-recording';
function read(body:DataEnvelope<CallAudioRecording>):CallAudioRecording{
 const value=body?.data;
 if(typeof value?.enabled!=='boolean'||!Number.isSafeInteger(value.version)||value.version<1)throw new Error('invalid_call_recording_response');
 return {enabled:value.enabled,version:value.version};
}

/** Server-owned workspace state: no local preference or optimistic success, and no mutation retry. */
export class CallRecordingControl{
 private state:CallRecordingState={value:null,phase:'loading',message:'',reauthenticate:false};
 private listeners=new Set<()=>void>();
 private request:AbortController|null=null;
 private generation=0;
 constructor(private api:Pick<ApiClient,'request'>){}
 snapshot=()=>this.state;
 subscribe=(listener:()=>void)=>{this.listeners.add(listener);return()=>{this.listeners.delete(listener);};};
 private set(value:CallRecordingState){this.state=value;for(const listener of this.listeners)listener();}
 cancel(){this.generation++;this.request?.abort();this.request=null;}
 async load(message=''){
  if(this.state.phase==='saving')return;
  this.cancel();const generation=this.generation,request=new AbortController();this.request=request;
  this.set({...this.state,phase:'loading',message,reauthenticate:false});
  try{
   const value=read(await this.api.request<DataEnvelope<CallAudioRecording>>(PATH,{signal:request.signal}));
   if(generation===this.generation)this.set({value,phase:'ready',message,reauthenticate:false});
  }catch{
   if(generation===this.generation)this.set({value:null,phase:'error',message:'Could not load call recording. Try again.',reauthenticate:false});
  }
 }
 async toggle(){
  if(this.state.phase!=='ready'||!this.state.value)return;
  const before=this.state.value;
  this.cancel();const generation=this.generation,request=new AbortController();this.request=request;
  this.set({...this.state,phase:'saving',message:'',reauthenticate:false});
  try{
   const value=read(await this.api.request<DataEnvelope<CallAudioRecording>>(PATH,{method:'PATCH',signal:request.signal,body:JSON.stringify({enabled:!before.enabled,expected_version:before.version})}));
   if(generation===this.generation)this.set({value,phase:'ready',message:'Saved for future calls in this workspace.',reauthenticate:false});
  }catch(error){
   if(generation!==this.generation)return;
   const code=error instanceof ApiRequestError?error.code:'';
   const reauthenticate=code==='reauthentication_required'||code==='mfa_required';
   const message=reauthenticate?'Sign in again to change call recording.':code==='version_conflict'
    ?'Another admin changed this setting. The current value is shown.'
    :'Could not confirm the change. Check the current setting before trying again.';
   this.set({value:null,phase:'error',message,reauthenticate});
   if(!reauthenticate&&code!=='permission_denied')await this.load(message);
  }
 }
}
